import React, {useMemo, useState} from 'react';
import {Box} from '@mui/material';
import SecuritygroupHeader from './Header/SecurityGroupHeader';
import SecurityGroupBottom from './bottom/SecurityGroupBottom';
import ViewSecurityGroupGrid from '../SecurityGroups/component/ViewSecurityGroupGrid';
import './ViewSecuritygroup.scss';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../shared/interfaces';
import {t} from 'i18next';
import {convertToAccessControlRequest} from '../../components/SecurityGroups/component/securityGroupUtility';
import {useSaveAccessControlListData} from '../../hooks/securityGroup/useSecurityGroup';
import ArrModal from './../common/arr-modal/ArrModal';
import {useSecurityGroupAccessControls} from '../../hooks/securityGroup/useSecurityGroups';
const ViewSecuritygroup: React.FC = () => {
  // Selected group
  const [selectedGroup, setSelectedGroup] = useState<string | null>('1');

  const {data: accessControlData, isFetching: isAccessControlFetching} =
    useSecurityGroupAccessControls(selectedGroup);

  const initialRadioOption: 'projectLevel' | 'securityGroupSpecific' =
    useMemo(() => {
      // If data is available, map 'isSecurityGroupLevel' boolean to option string
      if (accessControlData) {
        return accessControlData.isSecurityGroupLevel
          ? 'securityGroupSpecific'
          : 'projectLevel';
      } // Default to projectLevel if no data/loading
      return 'projectLevel';
    }, [accessControlData]);

  // Right-side data (from all tabs)
  const [allRightData, setAllRightData] = useState<
    Record<string, TreeViewBaseItem<ExtendedTreeItemProps>[]>
  >({});
  // Modal control
  const [showRegenerationModal, setShowRegenerationModal] = useState(false);

  // Track if user made any change in the right tree
  const [isModified, setIsModified] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  // Toast notification states
  const [showToastNotification, setShowToastNotification] = useState(false);
  const [toastNotificationMessage, setToastNotificationMessage] = useState('');
  const [toastNotificationType, setToastNotificationType] = useState<
    ToastType | ''
  >('');
  const {mutate: saveAccessControlListData} = useSaveAccessControlListData();
  const [refreshKey, setRefreshKey] = useState(0);

  // Save data function
  const SaveData = (isSecurityGroupSpecific: boolean) => {
    const securityGroupID = Number(selectedGroup) || 1;
    const securityGroupName = '';

    const requestPayload = convertToAccessControlRequest(
      allRightData,
      securityGroupID,
      securityGroupName,
      isSecurityGroupSpecific
    );
    saveAccessControlListData(requestPayload, {
      onSuccess: () => {
        setShowToastNotification(true);
        setToastNotificationMessage(t('securityGroup.successSaveMessage'));
        setToastNotificationType(ToastType.Success);
        setIsModified(false);
        setIsSaveEnabled(false);
        setRefreshKey((prev) => prev + 1);
      },
      onError: () => {
        setShowToastNotification(true);
        setToastNotificationMessage(t('securityGroup.failureMessage'));
        setToastNotificationType(ToastType.Error);
      },
    });
  };

  const handleGroupCreated = () => {
    setIsSaveEnabled(true);
  };

  // Handle save button click
  const handleSave = (isSecurityGroupSpecific: boolean) => {
    if (isSecurityGroupSpecific) {
      setShowRegenerationModal(true);
    } else {
      SaveData(false);
    }
  };

  // Modal actions
  const handleCloseRegenerationModal = () => {
    setShowRegenerationModal(false);
  };

  const handleConfirmRegeneration = () => {
    setShowRegenerationModal(false);
    SaveData(true); // only save if user confirms
  };

  const regenerationModalButtons = [
    {
      id: 'cancel-regeneration',
      label: t('common.no'),
      onClick: handleCloseRegenerationModal,
      variant: 'secondary' as const,
      dataTestId: 'vsg-regen-cancel',
    },
    {
      id: 'confirm-regeneration',
      label: t('common.yes'),
      onClick: handleConfirmRegeneration,
      variant: 'primary' as const,
      dataTestId: 'vsg-regen-confirm',
    },
  ];

  const handleRightDataChange = (
    updatedData: Record<string, TreeViewBaseItem<ExtendedTreeItemProps>[]>
  ) => {
    setAllRightData(updatedData);
    setIsModified(true);
    setIsSaveEnabled(true);
  };

  return (
    <Box className="view-security-group">
      {/* Header */}
      <SecuritygroupHeader
        selectedGroupID={selectedGroup ? {id: selectedGroup, name: ''} : null}
        onGroupChange={setSelectedGroup}
        onGroupCreated={handleGroupCreated}
      />

      {/* Grid component */}
      <ViewSecurityGroupGrid
        selectedGroup={selectedGroup}
        onRightDataChange={handleRightDataChange}
        onTreeViewSelectionFlag={(hasSelection) => setIsModified(hasSelection)}
        refreshKey={refreshKey}
      />

      <SecurityGroupBottom
        onSave={handleSave}
        isModified={isModified}
        isSaveEnabled={isSaveEnabled}
        currentSecurityGroupId={
          selectedGroup && !isNaN(Number(selectedGroup))
            ? Number(selectedGroup)
            : null
        }
        initialSelectedOption={initialRadioOption}
        isDataLoading={isAccessControlFetching}
      />

      {/* Toast notification */}
      {showToastNotification && (
        <ArrToastNotification
          message={toastNotificationMessage}
          status={[toastNotificationType]}
          onClose={() => setShowToastNotification(false)}
        />
      )}

      {/* Regeneration confirmation modal */}
      <ArrModal
        showModal={showRegenerationModal}
        title={t('securityGroup.securitygroupSpecificHeader')}
        showClose
        onClose={handleCloseRegenerationModal}
        dataTestId="secgroup-view-modal"
        buttons={regenerationModalButtons}
        className="regeneration-confirm-modal"
        description={t('securityGroup.securitygroupSpecificMessage')}
      ></ArrModal>
    </Box>
  );
};

export default ViewSecuritygroup;
